import { EdocsSortOption } from "constants/clinics";
import type { HearingLossThresholds } from "constants/hearing-loss";
import type { PatientAlertPopupMode } from "constants/patient-alerts";

import type { StoredEmailBranding } from "./email-branding.type";
import { PriceTables } from "./price-tables.type";
import { QuickBooksToken } from "./quickbooks.type";
import { Region } from "./regions.type";
import { StaffMember } from "./staff.type";

export type IntakePhysicianHandling = "auto_create" | "review" | "never_create";

export interface Practice {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  tilled_account_id: string;
  justifi_account_id: string;
  gravity_account_id?: string | null;
  card_processing_provider?: number;
  surcharge_percent?: string | number;
  logo?: string;
  // Practice-supplied payment QR image (e.g. a Zelle QR) rendered on invoices/statements
  // instead of the generated portal payment-link QR. Read-only; uploaded via Django admin
  // (SN-19266). Optional key: absent on backends that predate the field.
  custom_payment_qr_code?: string | null;
  has_quickbooks_token: boolean;
  is_quickbooks_enabled: boolean;
  country: string;
  no_reply_email?: string | null;
  suno_comms_id?: string | null;
  scheduling_allow_overlap?: boolean;
  scheduling_cross_clinic_conflict_check?: boolean;
  scheduling_ignore_room_reservation?: boolean;
  scheduling_should_create_schedule_for_staff?: boolean;
  scheduling_should_notify?: boolean;
  scheduling_staff_availability?: boolean;
  scheduling_cell_duration?: number;
  /** ENG-2105: dedicated slots accept only their own types unless the user can override. */
  scheduling_enforce_dedicated_slots?: boolean;
  /** ENG-2014: only admins and business owners are offered Manage Work Shifts. */
  only_admins_manage_work_shifts?: boolean;
  /**
   * ENG-2149: when false, nobody at the practice is offered Scheduling V3 (read by
   * `useSchedulerChoice`). Absent on a backend without migration 0476, which reads as on.
   */
  new_scheduler_enabled?: boolean;
  /**
   * ENG-2277: the practice may convert PDFs into Forms (the converter's server-side gate, as it
   * spends AI credits). Absent on a backend without migration accounts/0482, which reads as off.
   */
  html_forms_enabled?: boolean;
  /**
   * DEV-3110: practice-wide mode of the patient-alert review pop-up
   * (`constants/patient-alerts`). Undefined = backend predates the field = off.
   */
  patient_alert_popup_mode?: PatientAlertPopupMode;
  // Both edited in Settings → Practice → General → Required fields. Null until first saved.
  patient_settings: {
    required_fields: {
      referral_source: boolean | null;
      sex: boolean | null;
      sub_referral_source: boolean | null;
      /** Not read anywhere and not offered in Settings; kept so a save preserves it. */
      birthdate: boolean | null;
    } | null;
  } | null;
  hearing_loss_thresholds?: HearingLossThresholds | null;
  /**
   * SN-19904. Read it through `useEmailBranding`, which applies the defaults. Absent on a backend
   * that predates the field, which is also what keeps every email-branding write off the wire.
   */
  email_branding?: StoredEmailBranding | null;
  appointment_settings: {
    required_fields: {
      /** Replaced the `mandatoryReferralSourceOnAppointmentModal` LD flag. */
      referral_source?: boolean | null;
      sub_referral_source: boolean | null;
    } | null;
  } | null;
  // Optional key: absent on backends that predate the field (deploy-ordering
  // safety — see use-practice-details-form.hook.tsx).
  staff_task_settings?: {
    required_fields: {
      due_at: boolean;
      priority: boolean;
    } | null;
  } | null;
  sale_lock_days?: number;
  unit_price_lock?: boolean;
  product_lock?: boolean;
  collect_outcome?: boolean;
  product_requests_deactivate_existing?: boolean;
  timezone?: string;
  is_messaging_enabled?: boolean;
  save_payment_card?: boolean;
  limit_to_current_clinic?: boolean;
  // Patient text routing (INBOX_TEXT_ROUTING_FEATURE): 1 = last contact (default), 2 = clinic
  // inbox owner. Absent on a backend that predates the field — treat as 1. Only ever PATCHed
  // with the flag on (ValidateUnknownFieldsMixin 400s an unknown key).
  // ENG-2223, read-only: some active clinic has an inbox owner, so the practice routes texts
  // (the Inbox's Mine / My clinics / Unassigned tabs). Replaces the old `text_routing_mode`.
  text_routing_active?: boolean;
  // ENG-2223, read-only (Suno sets it in Django admin): the AI "needs reply" labels run here.
  ai_message_tagging_enabled?: boolean;
  products_use_barcode_scanner?: boolean;
  go_live_at?: string;
  message_auto_read_enabled?: boolean;
  billing_use_practice_insurers_only?: boolean;
  create_all_sales_in_draft_status?: boolean;

  billing_available_payment_methods?: number[];
  billing_available_adjustment_types?: number[];
  use_room_traffic?: boolean;
  auto_create_patient_in_noah?: boolean;
  auto_create_physicians_from_intake?: boolean;
  intake_physician_handling?: IntakePhysicianHandling;
  use_unit_price_for_claims?: boolean;
  billing_default_payment_source_type?: number;
  billing_default_payment_method?: number;
  invoice_payment_terms_days?: number | null;
  default_preferred_communication_method?: number;
  box_default_folders?: string;
  box_default_subfolders?: Record<string, string[]>;
  edocs_sort_by?: EdocsSortOption;
  // New document notice: absent on a backend that predates it, which reads as off.
  show_new_edocs_notice?: boolean;
  billing_allowed_payment_plan?: number;
  google_review_request_message_disabled?: boolean;
  // SPA-6584: minutes after Completed before the review request (or the private feedback survey) goes out.
  google_review_request_delay_minutes?: number;
  // ENG-2680: 0 (off), 1 or 2 days after a visit left in Arrived/Ready. Writable.
  google_review_arrived_fallback_days?: number;
  // ENG-2679: read-only, a Suno switch in Django admin. Gates the setting above and the 1-day delay.
  google_review_scheduled_requests_enabled?: boolean;
  send_appointment_reminder_on_friday?: boolean;
  extended_reminder_day?: number | null;
  default_appointment_reminder_duration?: string | null;
  private_feedback_request_disabled?: boolean;
  appointment_cancelled_message_disabled?: boolean;
  /** "Ask each time": staff answer Yes/No to the cancellation message on every cancel/delete. */
  appointment_cancelled_message_prompt?: boolean;
  appointment_information_disabled?: boolean;
  notification_disabled?: boolean;
  /** SN-19964: booking, reschedule and reminder emails carry an appointment.ics. */
  appointment_calendar_invite_enabled?: boolean;
  no_show_disabled?: boolean;
  reminder_request_disabled?: boolean;
  reschedule_disabled?: boolean;
  intake_form_request_reminder_disabled?: boolean;
  onboarding_form_reminder_duration?: string | null;
  create_bills_on_clinic_stock_checkin?: boolean;
  sendbird_chat_enabled?: boolean;
  // Practice-level Suno Voice activation (Administration -> Voice Setup).
  // The only gate for every voice surface (SN-19621) — read it through
  // `usePracticeVoiceEnabled`; absent means off.
  voice_enabled?: boolean;
  // Hot desking (ENG-2293): Suno's "Log in on a desk phone" entry points, per
  // practice; toggled by Suno staff from Voice Setup. Read-only here.
  hot_desking_enabled?: boolean;
  post_insurance_payments_per_line_item?: boolean;
  generate_repair_form?: boolean;
  loss_damage_flow_enabled?: boolean;
  generate_exchange_form?: boolean;
  generate_loss_damage_form?: boolean;
  previous_oms?: number | null;
  use_apply_date_for_era?: boolean;
  enable_appointment_communications?: boolean;
  default_accept_assignment?: string | null;
  qbo_sync_refunds?: boolean;
  qbo_taxes_line_item_enabled_on?: string | null;
  allow_tilled_refund?: boolean;
  enforce_delivery_before_ready_to_bill?: boolean;
  stripe_customer_id?: string | null;
  include_draft_sales_in_revenue_calculations?: boolean;
  use_posted_dates_in_revenue_calculations?: boolean;
  warranty_dates_on_delivery_default?: boolean;
  enable_processing_fee?: boolean;
  processing_fee_percent?: number;
  // Native credit-card surcharge rate (%), set at onboarding via Django admin. Read-only in the FE
  // — display/disclosure mirror only; the charged amount comes from the processor response. 0 = none.
  surcharge_percent?: number;
  enable_darwill_statements_integration?: boolean;
  direct_mail_enabled?: boolean;
  enrolled_in_collections?: boolean;
  // Sales Coach per-practice toggles (ENG-1707) — read-only here; written via
  // Administration -> Coach Setup.
  live_coaching_enabled?: boolean;
  consult_debrief_enabled?: boolean;
  hide_global_tags?: boolean;
  show_deactivated_patients_in_search?: boolean;
  force_sso_only?: boolean;
  mfa_required?: boolean;
  is_claim_md_account_key_set?: boolean;
  has_active_provider_integration?: boolean;
  can_offer_online_deposit?: boolean;
  migrated_appointment_notifications_disabled?: boolean;
  npi?: string;
  ein?: string;
  billing_name?: string;
  billing_street_address_1?: string;
  billing_street_address_2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_country?: string;
  billing_zip_code?: string;
  billing_phone?: string;
  manufacturer_filter_enabled?: boolean;
  patient_portal_documents?: boolean;
  patient_portal_reports?: boolean;
  patient_portal_videos?: boolean;
  patient_portal_hide_paid_invoices?: boolean;
  show_patient_credit_on_documents?: boolean;
  patient_portal_hide_partial_payment?: boolean;
  enable_dashboard_widgets?: boolean;
  implementation_hub_visible?: boolean;
  training_enabled?: boolean;
  google_reviews_enabled?: boolean;
  enable_ai_review_replies?: boolean;
  /** Suno-controlled rollout switch for Real Ear Measurement in the audiology report (read-only; Django admin). */
  real_ear_measurement_enabled?: boolean;
  /**
   * Most patients a custom campaign that sends Text/SMS may target (read-only; Suno sets it per
   * practice in Django admin, default 400). The wizard's Filters step gates on it; absent on an
   * older backend, in which case `SMS_CAMPAIGN_MAX_RECIPIENTS` is the fallback.
   */
  custom_campaign_sms_max_recipients?: number;
  zoom_waiting_room_enabled?: boolean;
  zoom_join_before_host?: boolean;
  zoom_mute_on_entry?: boolean;
  form_rendering_defaults?: {
    custom_form?: FormRenderingOptions;
    purchase_agreement?: FormRenderingOptions;
  } | null;
}

export type FormRenderingOptions = {
  enhanced_table_rendering?: boolean | null;
  clinic_header?: boolean | null;
  provider_signature_in_footer?: boolean | null;
  patient_signature_in_footer?: boolean | null;
};

export interface PracticeBillingSettings {
  practice_id?: number;
  credit_card_payment_enabled?: boolean;
  subscription_payment_enabled?: boolean;
  terminal_payment_enabled?: boolean;
  online_payment_enabled?: boolean;
  allow_payment_save?: boolean;
}

export type ClinicResponse = {
  id: number;
  name: string;
  practice: Practice;
  created_at: string;
  updated_at: string;
  tilled_account_id?: string;
  justifi_account_id?: string;
  fax_phone?: string;
  /** Receive-only fax numbers; outbound always uses `fax_phone`. */
  additional_fax_phones?: string[];
  /** Receive-only texting numbers; outbound always uses `sms_phone`. */
  additional_sms_phones?: string[];
  card_processing_provider?: number;
  type?: ClinicTypes;
  is_active: boolean;
  external_id: string;
  display_name: string;
  logo?: string | null;
  caption_call_referral_code?: string;
  five_keys_url?: string | null;
  business_name?: string;
  patients_count?: number | null;
  // UNCONFIRMED/CONFIRMED appointments booked at the clinic from today on; same opt-in as
  // patients_count (what "Move patients" moves along with the patients). See SN-19777.
  upcoming_appointments_count?: number | null;
};

export interface Clinic {
  id: number;
  name: string;
  display_name: string;
  fullName: string;
  practice: Practice;
  tilledAccountId?: string;
  justifiAccountId?: string;
  gravity_account_id?: string | null;
  state?: string;
  street_address_1?: string;
  street_address_2?: string;
  zip_code?: string;
  city?: string;
  country?: string;
  npi?: string;
  phone?: string;
  email?: string | null;
  fax_phone?: string;
  /** Receive-only fax numbers; outbound always uses `fax_phone`. */
  additional_fax_phones?: string[];
  timezone?: string;
  created_at: string;
  updated_at: string;
  card_processing_provider?: number;
  noah_provider?: number | null;
  google_place_id?: string | null;
  scheduling_staff_availability?: boolean;
  is_active: boolean;
  external_id: string;
  type?: ClinicTypes;
  sms_phone?: string | null;
  /** Receive-only texting numbers; outbound always uses `sms_phone`. */
  additional_sms_phones?: string[];
  // Only present when fetched with `withClinicCounts(query)` (the admin panel's clinics list, both
  // tabs) and only for admin-level callers; null otherwise. Counts every non-discarded patient,
  // active or not. SN-19777.
  patients_count?: number | null;
  // UNCONFIRMED/CONFIRMED appointments booked at the clinic from today on; same opt-in as
  // patients_count (what "Move patients" moves along with the patients). See SN-19777.
  upcoming_appointments_count?: number | null;
  users?: StaffMember[];
  price_tables?: ClinicPriceTable[];
  region?: Region;
  logo?: string | null;
  product_requests_deactivate_existing?: boolean;
  extra?: {
    id?: number;
    billng_city: string;
    billng_country: string;
    billng_phone: string;
    billng_state: string;
    billng_street_address_1: string;
    billng_street_address_2: string;
    billng_zip: string;
    ontario_adp_vendor_reg_number?: string;
    include_tympanometry_graph?: boolean;
    /** Default top of the tympanogram y-axis (a floor — the axis still expands to fit taller curves). */
    tympanometry_scale_max?: number;
    /** Spacing between tympanogram y-axis gridlines. */
    tympanometry_scale_step?: number;
    instructions?: string;
  } | null;
  box_folder_id?: string | null;
  noah_tenant_id?: string;
  ein?: string;
  caption_call_referral_code?: string;
  five_keys_url?: string | null;
  business_name?: string;
  is_claim_md_account_key_set?: boolean;
  has_active_provider_integration?: boolean;
  dba?: string;
  opened_date?: string | null;
  closed_date?: string | null;
  non_npi_id?: string;
  non_npi_id_qualifier?: string;
  own_ein?: string;
  own_npi?: string;
  own_non_npi_id?: string;
  own_non_npi_id_qualifier?: string;
  /**
   * Raw clinic-level values, with no practice fallback — unlike `display_name` and
   * `default_appointment_reminder_duration`, which resolve through the practice when the
   * clinic has nothing set. Always edit these in the admin modal: writing back a rendered fallback
   * value stamps the practice's value onto the clinic and detaches it from the fallback.
   */
  own_display_name?: string | null;
  own_default_appointment_reminder_duration?: string | null;
  review_link?: string;
  no_reply_email?: string;
  is_messaging_enabled?: boolean;
  is_emailing_enabled?: boolean;
  default_appointment_reminder_duration?: string | null;
  noah_alias?: string | null;
  noah_auto_sync?: boolean;
  web_scheduling_staff_selection?: boolean;
  place_of_service?: number;
  billing_name?: string;
  intake_form_completion_email_recipients?: string[];
  default_assignee?: StaffMember | null;
  web_scheduler_email_recipient?: StaffMember | null;
  web_scheduler_email_recipients?: StaffMember[];
  noah_business_admin?: StaffMember | null;
  noah_location_id?: string | null;
  // Write-only on the backend (SN-19151): a read never carries the value, only the boolean below.
  // Send a new key to replace it, `null` to clear it; an empty string is rejected with a 400.
  documo_account_key?: string | null;
  is_documo_account_key_set?: boolean;
  claim_md_account_key?: string;
  gravity_account_id?: string;
  quickbooks_realm_id?: string;
  suno_comms_id?: string;
  quickbooks_company?: {
    id: number;
    realm_id?: string;
    company_name?: string;
  } | null;
}

export interface ClinicPriceTable {
  clinic: number;
  price_table: PriceTables;
}

export type ClinicRequest = Omit<
  Clinic,
  | "users"
  | "created_at"
  | "id"
  | "updated_at"
  | "practice"
  | "fullName"
  | "price_tables"
  | "display_name"
  | "region"
  | "default_assignee"
  | "web_scheduler_email_recipient"
  | "web_scheduler_email_recipients"
  | "noah_business_admin"
> & {
  id?: number;
  quickbooks_token?: QuickBooksToken | null;
  /**
   * Create-only. Id of the clinic to copy system-managed fields from (logo, QuickBooks token +
   * company, card processing provider and the Tilled/Gravity/JustiFi account ids). Those fields are
   * read-only for API clients, so the backend copies them server-side.
   */
  duplicate_from?: number;
  users?:
    | { add: number[] }
    | { remove: number[] }
    | { add: number[]; remove: number[] };
  products?:
    | { add: number[] }
    | { remove: number[] }
    | { add: number[]; remove: number[] };
  price_tables?: number[];
  region?: number | null;
  default_assignee?: number | null;
  web_scheduler_email_recipient?: number | null;
  noah_business_admin?: number | null;
  web_scheduler_email_recipients?:
    | { add: number[] }
    | { remove: number[] }
    | { add: number[]; remove: number[] };
};

export type ClinicsResponse = Clinic[];

export type CardProcessingClinic = {
  id: number;
  name: string;
  street_address_1?: string;
  street_address_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  card_processing_provider?: number;
  tilled_account_id?: string;
  gravity_account_id?: string;
  // The clinic's own surcharge rate (null inherits the practice rate) and the resolved effective
  // rate. Both read-only; the clinic value is written only through updateClinicSurcharge.
  surcharge_percent?: string | null;
  effective_surcharge_percent?: string;
  practice: {
    id: number;
    name: string;
    card_processing_provider?: number;
    tilled_account_id?: string;
    gravity_account_id?: string;
    surcharge_percent?: string | number;
  };
};

export type UpdateCardProcessingAccountResponse = {
  id: number;
  card_processing_provider: number;
  tilled_account_id: string;
  gravity_account_id: string;
};

export interface ClinicFilters {
  search?: string;
  region?: number | number[] | null;
  type?: number | number[] | null;
  query?: string;
  user?: number;
  staff_member?: number;
  is_active?: boolean;
  qbo_company?: number;
}

export const enum ClinicTypes {
  FULL_TIME = 1,
  PART_TIME = 2,
  SERVICE_LOCATION = 3
}

export type TinyRegion = {
  id?: number;
  name: string;
};

export type TinyClinic = {
  id?: number;
  name: string;
  region?: TinyRegion;
};

// Preview for Administration > Clinics > "Move patients" (SN-19777): GET /clinics/:id/move-patients/.
export type ClinicMovePreviewTab = "patients" | "appointments";

export interface ClinicMovePreviewPatient {
  id: number;
  full_name: string;
  is_active: boolean;
  // Upcoming appointments this patient has at the clinic — what leaves with them if unticked.
  upcoming_appointments_count: number;
}

export interface ClinicMovePreviewAppointment {
  id: number;
  start_moment: string;
  status: number;
  patient_id: number;
  patient_name: string | null;
  staff_member_name: string;
  type_name: string | null;
  room_name: string | null;
  /** The clinic's zone, so the row's time reads as the clinic sees it (review D7). */
  clinic_timezone: string;
}

export interface ClinicMovePreview {
  // Totals the move covers, whatever the tab or search.
  patients_count: number;
  appointments_count: number;
  tab: ClinicMovePreviewTab;
  // Rows matching the tab + search, and one page of them.
  count: number;
  limit: number;
  offset: number;
  results: (ClinicMovePreviewPatient | ClinicMovePreviewAppointment)[];
}
